import {useCallback} from 'react';

import {BulkArchiveFilesOptions} from 'archive/ArchiveActions';
import {getArchiveAction, getUnarchiveAction} from 'archive/ArchiveActionsUtil';
import {assert} from 'Asserts';
import {ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import navigateToURL from 'lib/navigateToURL';
import TemplateCollectionDAO from 'notebook/entry-templates/collections/TemplateCollectionDAO';
import notifications from 'notifications';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  composeProcedureArchiveUnarchiveEventMetadata,
  composeProcedureTypeMetadata,
  PROCEDURE_ARCHIVE_UNARCHIVE_ENDED_EVENT,
  PROCEDURE_ARCHIVE_UNARCHIVE_STARTED_EVENT,
  PROCEDURE_MOVE_ENDED_EVENT,
  PROCEDURE_MOVE_STARTED_EVENT,
} from 'performance/processProceduresEvents';
import bufferForUX from 'util/bufferForUX';
import {withLoadingScreen} from 'util/Promises';

import {procedurePageUrl} from '../network/procedureUrls';
import {trackMovedProcedureEvent, trackToggledProcedureViewEvent} from '../procedureAnalyticsEventsUtil';
import {ProcedureFlowchart, ProcedureMetadata} from '../ProcedureDomainTypes';
import {BlockingSaveObjectSource, BlockingSaveOperation, PersistenceInfo} from '../ProcedurePageContext';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from '../ProceduresStringKeys';
import AutosaveManager from '../redux/autosaveManager';
import {addMethodToLinearFlowchart, AppendDirection, generateNewMethodCid} from './flowchartManagementUtils';
import {isNameEmpty} from './validation/validateProcedure';

export type ModifyFlowchartCallback = (fc: ProcedureFlowchart) => ProcedureFlowchart;

export interface AddMethodOptions {
  shouldSwitchViewToFlowchart?: boolean;
  shouldOpenNameEditor?: boolean;
}

export interface UseProcedureActionsParams {
  isProcedurePoW: boolean;
  procedureMetadata: ProcedureMetadata;
  setProcedureMetadata: (metadata: ProcedureMetadata) => void;
  legacySaveImmediately: (
    applyChanges: (latestFlowchart: ProcedureFlowchart) => PersistenceInfo,
    overlayMessage?: string,
    shouldBubbleUpErrors?: boolean
  ) => Promise<ProcedureMetadata>;
  procedureId: string;
  hardRefresh: () => void;
  setLocalDraftFlowchart: (updater: (fc: ProcedureFlowchart) => ProcedureFlowchart) => void;
  draftFlowchartRef: React.MutableRefObject<ProcedureFlowchart>;
  selectedMethodCid: string | null;
  setSelectedMethodCid: (cid: string | null) => void;
  autofocusEditNameOnNewMethod: (methodCid: string) => void;
}

export interface UseProcedureActionsReturn {
  handleRename: (newName: string) => Promise<void>;
  handleMove: (templateCollectionId: string) => Promise<void>;
  handleArchive: (itemIds: Array<string>, purpose: string, options: BulkArchiveFilesOptions) => Promise<void>;
  handleUnarchive: () => Promise<void>;
  handleLegacyModifyFlowchart: (cb: ModifyFlowchartCallback) => void;
  handleLegacyStopEditingMethod: () => void;
  handleLegacyStartEditingMethod: (methodCid: string) => void;
  handleLegacyAddMethod: (options: AddMethodOptions) => void;
}

/**
 * Hook that provides all action handlers for procedure operations, including:
 * - Metadata operations (rename, move)
 * - Archive/unarchive operations
 * - Flowchart modification
 * - Method editing operations
 */
export default function useProcedureActions({
  isProcedurePoW,
  procedureMetadata,
  setProcedureMetadata,
  legacySaveImmediately,
  procedureId,
  hardRefresh,
  setLocalDraftFlowchart,
  draftFlowchartRef,
  selectedMethodCid,
  setSelectedMethodCid,
  autofocusEditNameOnNewMethod,
}: UseProcedureActionsParams): UseProcedureActionsReturn {
  const handleRename = useCallback(
    async (newName: string): Promise<void> => {
      const trimmedNewName = newName.trim();
      const isValidName = !isNameEmpty(trimmedNewName);

      if (!isValidName) {
        notifications.error(`Procedure names must be non-empty`, {duration: 7});
        return;
      }

      // In PoW, the `setProcedureMetadata` call will manage the save operation, which is immediate but non-blocking
      if (isProcedurePoW) {
        setProcedureMetadata({...procedureMetadata, name: trimmedNewName});
      } else {
        await legacySaveImmediately(() => {
          setProcedureMetadata({...procedureMetadata, name: trimmedNewName});
          return {
            blockingSaveObjectSource: BlockingSaveObjectSource.Metadata,
            blockingSaveOperation: BlockingSaveOperation.Rename,
          };
        }, AutosaveManager.NO_OVERLAY);
      }
    },
    [procedureMetadata, legacySaveImmediately, setProcedureMetadata, isProcedurePoW]
  );

  const handleMove = useCallback(
    async (templateCollectionId: string): Promise<void> => {
      logProfilerTimingEvent(PROCEDURE_MOVE_STARTED_EVENT);

      // In PoW, the `setProcedureMetadata` call will manage the save operation, which is immediate but non-blocking
      if (isProcedurePoW) {
        setProcedureMetadata({...procedureMetadata, templateCollectionId});
      } else {
        await legacySaveImmediately(
          () => {
            setProcedureMetadata({...procedureMetadata, templateCollectionId});
            return {
              blockingSaveObjectSource: BlockingSaveObjectSource.Metadata,
              blockingSaveOperation: BlockingSaveOperation.Move,
            };
          },
          `Moving ${getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureMetadata.procedureType, {
            stringCase: StringCase.LOWER,
          })} to different template collection...`
        );
      }

      notifications.success(
        `${getProcedureUIString(
          ProceduresStringKeys.PROCEDURE,
          procedureMetadata.procedureType
        )} was moved succesfully.`
      );
      trackMovedProcedureEvent(
        procedureId,
        procedureMetadata.procedureType,
        procedureMetadata.templateCollectionId,
        templateCollectionId
      );
      const eventMetadata = composeProcedureTypeMetadata(procedureMetadata.procedureType);
      logProfilerTimingEvent(PROCEDURE_MOVE_ENDED_EVENT, eventMetadata);

      // update active template collection shown in the settings sidebar
      navigateToURL(TemplateCollectionDAO.collectionURL(templateCollectionId));
      navigateToURL(procedurePageUrl(procedureId));
    },
    [procedureMetadata, setProcedureMetadata, legacySaveImmediately, procedureId, isProcedurePoW]
  );

  const handleArchive = useCallback(
    async (itemIds: Array<string>, purpose: string, options: BulkArchiveFilesOptions): Promise<void> => {
      logProfilerTimingEvent(PROCEDURE_ARCHIVE_UNARCHIVE_STARTED_EVENT);
      const archiveAction = (): Promise<void> =>
        new Promise((resolve) => {
          resolve(getArchiveAction(itemIds)(itemIds, purpose, options));
        });
      await withLoadingScreen(bufferForUX(archiveAction()), 'Archiving...');
      const eventMetadata = composeProcedureArchiveUnarchiveEventMetadata(
        true,
        procedureMetadata.procedureType
      );
      logProfilerTimingEvent(PROCEDURE_ARCHIVE_UNARCHIVE_ENDED_EVENT, eventMetadata);
      hardRefresh();
    },
    [procedureMetadata.procedureType, hardRefresh]
  );

  const handleUnarchive = useCallback(async (): Promise<void> => {
    logProfilerTimingEvent(PROCEDURE_ARCHIVE_UNARCHIVE_STARTED_EVENT);
    const unarchiveAction = (): Promise<void> =>
      new Promise((resolve) => {
        resolve(getUnarchiveAction([procedureId])([procedureId]));
      });
    await withLoadingScreen(bufferForUX(unarchiveAction()), 'Unarchiving...');
    const eventMetadata = composeProcedureArchiveUnarchiveEventMetadata(
      false,
      procedureMetadata.procedureType
    );
    logProfilerTimingEvent(PROCEDURE_ARCHIVE_UNARCHIVE_ENDED_EVENT, eventMetadata);
    hardRefresh();
  }, [procedureId, procedureMetadata.procedureType, hardRefresh]);

  const handleLegacyModifyFlowchart = useCallback(
    (cb: ModifyFlowchartCallback): void => {
      assert(!isProcedurePoW, 'handleLegacyStopEditingMethod should not be called for PoW procedures');
      setLocalDraftFlowchart(() => cb(draftFlowchartRef.current));
    },
    [setLocalDraftFlowchart, draftFlowchartRef, isProcedurePoW]
  );

  const handleLegacyStopEditingMethod = useCallback((): void => {
    assert(!isProcedurePoW, 'handleLegacyStopEditingMethod should not be called for PoW procedures');
    if (selectedMethodCid != null) {
      // Only track event when we've actually switched back to the flowchart view
      trackToggledProcedureViewEvent(
        procedureId,
        procedureMetadata.procedureType,
        ActionOrigin.PROCEDURE_DESIGNER_DETAILS
      );
    }
    setSelectedMethodCid(null);
  }, [procedureId, selectedMethodCid, procedureMetadata.procedureType, setSelectedMethodCid, isProcedurePoW]);

  const handleLegacyStartEditingMethod = useCallback(
    (methodCid: string): void => {
      assert(!isProcedurePoW, 'handleLegacyStartEditingMethod should not be called for PoW procedures');
      setSelectedMethodCid(methodCid);
      trackToggledProcedureViewEvent(
        procedureId,
        procedureMetadata.procedureType,
        ActionOrigin.PROCEDURE_DESIGNER_FLOWCHART
      );
    },
    [procedureId, procedureMetadata.procedureType, setSelectedMethodCid, isProcedurePoW]
  );

  const handleLegacyAddMethod = useCallback(
    (options: AddMethodOptions): void => {
      assert(!isProcedurePoW, 'handleLegacyAddMethod should not be called for PoW procedures');
      if (options?.shouldSwitchViewToFlowchart) {
        handleLegacyStopEditingMethod();
      }
      const newMethodCid = generateNewMethodCid();

      handleLegacyModifyFlowchart((fc) => {
        const lastNode = fc.nodes.at(-1);
        assert(
          lastNode !== undefined,
          'There should be at least 1 node in flowchart to add from method select dropdown'
        );
        const {updatedFlowchart} = addMethodToLinearFlowchart(
          procedureMetadata.procedureType,
          fc,
          lastNode.cid,
          {
            appendDirection: AppendDirection.DOWNSTREAM,
            newMethodCid,
          }
        );
        if (options?.shouldOpenNameEditor) {
          autofocusEditNameOnNewMethod(newMethodCid);
        }
        return updatedFlowchart;
      });
    },
    [
      isProcedurePoW,
      procedureMetadata.procedureType,
      handleLegacyStopEditingMethod,
      handleLegacyModifyFlowchart,
      autofocusEditNameOnNewMethod,
    ]
  );

  return {
    handleRename,
    handleMove,
    handleArchive,
    handleUnarchive,
    handleLegacyModifyFlowchart,
    handleLegacyStopEditingMethod,
    handleLegacyStartEditingMethod,
    handleLegacyAddMethod,
  };
}
